import { ComponentProps, Dispatch, FC, memo, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import {
  ChevronRight,
  DropdownDefault,
  DropdownOpen,
  Pencil,
} from '@factorialco/f0-react/icons/app'
import { CommunityHeaderDocument } from 'generated/resources/graphql'

import Popover from 'modules/core/components/Popover'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import accessGroups from 'modules/core/store/collections/accessGroups'

import { useCanCreatePosts } from 'modules/dashboard/components/Groups/GroupPicker/GroupInfo'
import GroupSelector from 'modules/dashboard/components/Groups/GroupPicker/GroupSelector'
import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import accessGroupMemberships from 'modules/dashboard/store/collections/AccessGroupMemberships'
import groups from 'modules/dashboard/store/collections/communities/Groups'
import { ManageMembersModal } from 'modules/posts/components/Groups/CommunityHeader/ManageMembersModal'

type CommunityHeaderProps = {
  selectedCommunity: CommunitiesDropdown
  onSelectedCommunity: Dispatch<CommunitiesDropdown>
  canCreateGroup: boolean
}

const CommunityHeader: FC<CommunityHeaderProps> = ({
  selectedCommunity,
  onSelectedCommunity,
  canCreateGroup,
}) => {
  const [open, toggle] = useToggle(false)
  const [isManageMembersOpen, setIsManageMembersOpen] = useState(false)

  const { access } = useCurrentSession()

  const currentUserId = access.id

  const canCreatePosts = useCanCreatePosts({ communitiesDropdown: selectedCommunity })
  const { has_dev_communities_page, has_dev_dynamic_communities } = useFeatures([
    'dev_communities_page',
    'dev_dynamic_communities',
  ])

  const { navigation } = useNavigation()

  const navigate = useNavigate()

  // When we create a new group, we need to get the accessGroup and
  // accessGroupMembership associted with the current user
  const groupsCount = groups.models.length

  const selectedCommunityId = selectedCommunity.value
  const { data } = useQuery(CommunityHeaderDocument, {
    variables: {
      id: String(selectedCommunityId),
    },
    skip: !selectedCommunityId,
  })

  const selectedCommunityGroup = data?.posts.group
  const canUpdateGroup = selectedCommunityGroup?._policies?.update.can

  const canManageParticipants = canUpdateGroup // The update policy does the same as update. TODO: Unify those policies at the BE
  const canArchiveGroup = canUpdateGroup // The archive policy does the same as UPDATE. TODO: Unify those policies at the BE

  useEffect(() => {
    if (!currentUserId || !groupsCount) return

    accessGroups.fetch()
    accessGroupMemberships.fetch({
      data: {
        access_id: currentUserId,
      },
    })
  }, [groupsCount, currentUserId])

  const displayTitle = selectedCommunity.title || i18n.t('posts.all_communities.title')

  const toggleCommunityPicker = () => {
    toggle(false)()
  }

  const handleClickCommunityPicker = () => {
    track(trackingEvents.dashboard.clickOnCommunityPicker)
    toggleCommunityPicker()
  }

  const handleClickCreatePost = () => {
    track(trackingEvents.dashboard.clickOnCreateNewPost)
    navigate(navigation.dashboard.post.new.fullPath())
  }

  const handleClickGoToCommunities = () => {
    navigate(navigation.communities.fullPath())
  }

  const createGroup = () => {
    navigate(navigation.dashboard.groups.new.fullPath())
    toggleCommunityPicker()
  }

  const joinGroup = () => {
    navigate(navigation.dashboard.groups.join.fullPath())
    toggleCommunityPicker()
  }

  const handleSelectGroup = (community: CommunitiesDropdown) => {
    onSelectedCommunity?.(community)
    toggleCommunityPicker()
  }

  const communityManagementOptions = useMemo(() => {
    if (!selectedCommunityId) return []

    const options: ComponentProps<typeof Dropdown>['items'] = [
      canUpdateGroup && {
        label: i18n.t('posts.communities.edit_group'),
        href: navigation.dashboard.groups.id.edit.fullPath({ id: selectedCommunityId }),
      },
      canManageParticipants && {
        label: i18n.t('posts.communities.manage_members'),
        onClick: () => setIsManageMembersOpen(true),
      },
      {
        label: i18n.t('posts.communities.leave_group'),
        href: navigation.dashboard.groups.id.leave.fullPath({ id: selectedCommunityId }),
      },
      canArchiveGroup && {
        label: i18n.t('posts.communities.delete_group'),
        href: navigation.dashboard.groups.id.delete.fullPath({ id: selectedCommunityId }),
        critical: true,
      },
    ].filter(Boolean)

    return options
  }, [navigation, selectedCommunityId, canManageParticipants, canArchiveGroup, canUpdateGroup])

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Popover size="big" attachment="top center" open={open} toggle={toggle}>
        <Box
          inline
          flexDirection="row"
          alignItems="center"
          gap="s4"
          onClick={handleClickCommunityPicker}
          onBlur={toggleCommunityPicker}
        >
          <span className="text-2xl text-f1-foreground font-semibold cursor-default">
            {displayTitle}
          </span>
          <F0Icon icon={open ? DropdownOpen : DropdownDefault} size="lg" />
        </Box>
        <Box padding="s24">
          <GroupSelector
            createGroup={createGroup}
            joinGroup={joinGroup}
            onSelect={handleSelectGroup}
            selectedGroup={selectedCommunity}
            excludeCelebrationGroups
            canCreateGroup={canCreateGroup}
          />
        </Box>
      </Popover>
      <Box flexDirection="row" alignItems="center" gap="s4">
        {canCreatePosts && (
          <>
            <F0Button
              label={i18n.t('new_dashboard.posts.write_post')}
              onClick={handleClickCreatePost}
              icon={Pencil}
              variant="outline"
            />
            {has_dev_communities_page && (
              <F0Button
                icon={ChevronRight}
                label={i18n.t('posts.all_communities.title')}
                variant="outline"
                hideLabel
                onClick={handleClickGoToCommunities}
              />
            )}
          </>
        )}
        {!!communityManagementOptions.length && <Dropdown items={communityManagementOptions} />}
      </Box>
      {selectedCommunityId && canManageParticipants && (
        <ManageMembersModal
          groupId={String(selectedCommunityId)}
          groupTitle={selectedCommunity.title}
          isOpen={isManageMembersOpen}
          onOpenChange={setIsManageMembersOpen}
          canManageMembers
          isDynamicCommunitiesEnabled={has_dev_dynamic_communities}
          dynamicTeamId={selectedCommunityGroup?.dynamicTeamId ?? null}
        />
      )}
    </Box>
  )
}

export default memo(CommunityHeader)
